package nl.volleyanalyzer.web.controller

import kotlinx.serialization.Serializable
import nl.volleyplanner.api.volleyanalyzer.PouleMatchesResponse
import nl.volleyplanner.api.sports.ScoringMethod
import nl.volleyplanner.util.mapWithPrevious

@Serializable
data class PouleStandingRecord(
    val team: String,
    val playedThisRound: Boolean,
    val matchesPlayed: Int,
    val points: Int,

    val setsWon: Double,
    val setsLost: Double,

    val pointsWon: Int,
    val pointsLost: Int
) {
    val setQuotient = setsWon / setsLost.coerceAtLeast(1.0)
    val pointsQuotient = pointsWon.toDouble() / pointsLost.toDouble().coerceAtLeast(1.0)

    companion object {
        fun emptyRecord(team: String) = PouleStandingRecord(team, false, 0, 0, 0.0, 0.0, 0, 0)
    }

    operator fun plus(record: PouleStandingRecord): PouleStandingRecord {
        return PouleStandingRecord(
            team = team, playedThisRound = record.playedThisRound, matchesPlayed = matchesPlayed + record.matchesPlayed,
            points = points + record.points, setsWon = setsWon + record.setsWon, setsLost = setsLost + record.setsLost,
            pointsWon = pointsWon + record.pointsWon, pointsLost = pointsLost + record.pointsLost)
    }
}

@Serializable
data class PouleStandings(
    val round: Int,
    val teams: Set<String>,
    /**
     * Records by team, should be inserted into order of position
     */
    val records: LinkedHashMap<String, PouleStandingRecord>
)

@Serializable
data class MultiRoundPouleStandings(
    val pouleCode: String,
    val pouleName: String,
    val scoringMethod: ScoringMethod?,
    val teams: Set<String>,
    val roundStandings: LinkedHashMap<Int, PouleStandings>
) {
    fun lastRoundStandings() : PouleStandings {
        return roundStandings.values.last()
    }
}

fun PouleMatchesResponse.calculatePouleStandings() : MultiRoundPouleStandings? {
    val scoringMethod = fullPoule.scoringMethod ?: return null
    val matchesByTeams = this.matches
        .flatMap { match -> listOf(match.teamHome to match, match.teamGuest to match) }
        .groupBy({ it.first }, { it.second })
        .mapValues { (team, matches) ->
            val notPlayedRecord = PouleStandingRecord(team = team, playedThisRound = false, matchesPlayed = 0, points = 0, setsWon = 0.0, setsLost = 0.0, pointsWon = 0, pointsLost = 0)
            matches.sortedBy { it.startDateTime }
                .mapWithPrevious(notPlayedRecord) { index, match, previous ->
                    val teamIdentifier = match.identifyTeam(team)
                    val record = if (match.hasScore()) {
                        val setsWon = match.getSets(teamIdentifier)!!
                        val setsLost = match.getSets(!teamIdentifier)!!
                        val pointsWon = match.getPoints(teamIdentifier)!!
                        val pointsLost = match.getPoints(!teamIdentifier)!!

                        val matchCompleted = scoringMethod.isValidScore(setsWon, setsLost)

                        if (matchCompleted) {
                            val matchPoints = scoringMethod.calculatePoints(setsWon, setsLost).first
                            PouleStandingRecord(
                                team = team, playedThisRound = true, matchesPlayed = 1,
                                points = matchPoints, setsWon = setsWon, setsLost = setsLost,
                                pointsWon = pointsWon, pointsLost = pointsLost
                            )
                        } else notPlayedRecord
                    } else notPlayedRecord
                    previous + record
                }
        }

    val result = LinkedHashMap<Int, PouleStandings>()
    var round = 0
    while(true) {
        val standings = PouleStandings(
            round = round,
            teams = matchesByTeams.keys,
            records = matchesByTeams
                .map { (team, records) ->
                    val record = records.getOrElse(round, { records.last() + PouleStandingRecord.emptyRecord(team) })
                    Pair(team, record)
                }
                .sortedWith(compareByDescending<Pair<String, PouleStandingRecord>> { it.second.points }.thenByDescending { it.second.setQuotient }.thenByDescending { it.second.pointsQuotient }.thenBy { it.second.team })
                .toMap(LinkedHashMap())
        )
        if (round != 0 && standings.records.all { !it.value.playedThisRound }) break
        if (round == 0) {
            // Insert an empty record as round 0, so that we can display teams who didn't play any match yet
            // Insertion order as for round 1, so that we don't have mixed lines to start with
            result.put(
                round,
                PouleStandings(
                    round = round,
                    teams = matchesByTeams.keys,
                    records = standings.records.map { (team, record) ->
                        Pair(team, PouleStandingRecord.emptyRecord(team))
                    }.toMap(LinkedHashMap())
                )
            )
        }
        result.put(round + 1, standings)
        ++round
    }
    return MultiRoundPouleStandings(
        pouleName = fullPoule.name,
        pouleCode = fullPoule.pouleCode,
        scoringMethod = scoringMethod,
        teams = matchesByTeams.keys,
        roundStandings = result
    )
}
